import moment from 'moment-timezone';
import { encrypt } from 'n-krypta';
import {
  baseUrl,
  NETWORK_ERROR,
  REACT_APP_KEY,
  documentTypes,
  documentStatusText,
  erpTypes
} from '../constants/appConstants';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import {
  InsertDriveFileOutlined,
  FindInPageOutlined,
  CorporateFare,
  CloseOutlined,
  PanToolOutlined,
  DashboardOutlined,
  EmailOutlined,
  PersonOutlineOutlined,
  Pages,
  HighlightAltOutlined,
  AssignmentLateOutlined,
  Error,
  FileDownloadOutlined,
  AttachMoney,
  ShoppingCart,
  DescriptionOutlined,
  RequestQuoteOutlined,
  SquareFoot,
  Apartment,
  MonitorHeartOutlined,
  CategoryOutlined,
  PaidOutlined,
  AddCircleOutlined,
  LocalOfferOutlined
} from '@mui/icons-material';

import { styled } from '@mui/material';
import { useSelector } from 'react-redux';

import { Colors } from '../config/default';
import MuiModal from '../components/muiModel';
import { isEmpty } from 'lodash';
import { formatDistributedValue } from '../utils/canvasUtils';
//dev
const BASE_URL = baseUrl();
function buildApiEndpoint(path) {
  const url = new URL(path, BASE_URL);
  return url.href;
}
const { ESTIMATE, DELIVERY_TICKET, PURCHASE_ORDER } = documentTypes;
const { QUICKBOOKS_DESKTOP_ERP } = erpTypes;
const {
  IN_REVIEW,
  APPROVED,
  REJECT,
  WAITING,
  ONHOLD,
  POPULATED,
  EXPORT,
  ATTACHED,
  IN_QNA,
  PENDING,
  DUPLICATE,
  ERROR,
  PROCESSING,
  EXPORTED,
  REJECTED
} = documentStatusText;

export const iconMap = {
  Description: DescriptionOutlined,
  Unit_Price: AttachMoney,
  Quantity: ShoppingCart,
  Amount: AttachMoney,
  Product_Code: DescriptionOutlined,
  itemReference: DescriptionOutlined,
  userCostId: RequestQuoteOutlined,
  userCostType: RequestQuoteOutlined,
  userPhases: InsertDriveFileOutlined,
  userJobId: InsertDriveFileOutlined,
  GlAccount: DescriptionOutlined,
  equipmentNo: InsertDriveFileOutlined,
  serviceCode: DescriptionOutlined,
  glDivision: RequestQuoteOutlined,
  division2: RequestQuoteOutlined,
  division3: RequestQuoteOutlined,
  division4: RequestQuoteOutlined,
  UOM: SquareFoot,
  workOrder: InsertDriveFileOutlined,
  scope: InsertDriveFileOutlined,
  type: CategoryOutlined,
  tax: PaidOutlined,
  tax_code: RequestQuoteOutlined,
  tax_type: RequestQuoteOutlined,
  miscellaneous_amount: AddCircleOutlined,
  discount: LocalOfferOutlined
};

export const request = async (
  path,
  method = 'GET',
  body = {},
  contentType = 'application/json'
) => {
  const fetchOption = {
    method,

    headers: new Headers({
      Accept: 'application/json',
      'Content-Type': contentType
    }),
    ...(method !== 'GET' ? { body: JSON.stringify(body) } : {})
  };
  try {
    const res = await fetch(buildApiEndpoint(path), fetchOption);

    if (res.status === 200) {
      const jsonResponse = await res.json();
      return jsonResponse;
    } else {
      return res;
    }
  } catch (err) {
    alert(NETWORK_ERROR);
    throw new Error(err);
  }
};
export const setHeaders = () => {
  const headerConfig = {
    headers: { Authorization: 'Bearer ' + sessionStorage.getItem('token') }
  };
  return headerConfig;
};
export const setSignOutHeaders = () => {
  const headerConfig = {
    headers: { Authorization: 'Bearer ' + sessionStorage.getItem('accessToken') }
  };
  return headerConfig;
};

// sessionStorage.setItem stringifies, so writing an absent ERP type stores the
// literal string "undefined" — which then rode out on every request as
// `ErpType: undefined` for the rest of the session and had the backend key ERP
// behaviour off a garbage value. Write nothing when we don't know the ERP type.
export const setErpTypeInSession = (erpType) => {
  if (erpType) {
    sessionStorage.setItem('erpType', erpType);
  } else {
    sessionStorage.removeItem('erpType');
  }
};

// Reads the ERP type, treating a poisoned "undefined"/"null" left behind by an
// older session the same as an absent one.
export const getErpTypeFromSession = () => {
  const erpType = sessionStorage.getItem('erpType');
  return erpType && erpType !== 'undefined' && erpType !== 'null' ? erpType : null;
};

export const setERPType = () => {
  const erpType = getErpTypeFromSession();
  // Omit the header entirely rather than sending a value we don't have.
  const headerConfig = {
    headers: erpType ? { ErpType: erpType } : {}
  };
  return headerConfig;
};
export function formatDateOnly(dateString) {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}-${year}`;
}
export const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '-') {
    return '-';
  }
  const date = moment(
    dateStr,
    ['DD/MM/YYYY', 'D MMMM YYYY', moment.ISO_8601, 'ddd, DD MMM YYYY HH:mm:ss [GMT]'],
    true
  );
  if (date.isValid()) {
    const localTz = moment.tz.guess();
    return date.tz(localTz).format('MM/DD/YYYY, h:mm A z');
  } else {
    return '-';
  }
};
export const formatDateWithoutTime = (dateStr) => {
  if (!dateStr || dateStr === '-') {
    return '-';
  }
  const date = moment(
    dateStr,
    ['DD/MM/YYYY', 'D MMMM YYYY', moment.ISO_8601, 'ddd, DD MMM YYYY HH:mm:ss [GMT]'],
    true
  );
  if (date.isValid()) {
    return date.format('MM/DD/YYYY');
  } else {
    return '-';
  }
};
export const hashEmailAndPassword = (email, password) => {
  const salt = REACT_APP_KEY;
  const hashedEmail = encrypt(email, salt);
  const hashedPassword = encrypt(password, salt);
  return { hashedEmail, hashedPassword };
};

export const hashPassword = (password) => {
  const salt = REACT_APP_KEY;
  const newPass = encrypt(password, salt);
  return newPass;
};

export const hashEmail = (email) => {
  const salt = REACT_APP_KEY;
  const newEmail = encrypt(email, salt);
  return newEmail;
};

const childTabs = [
  { name: 'To Review' },
  { name: 'In Q&A' },
  { name: 'In Review' },
  { name: 'Classify/Split' },
  { name: 'Rejected' },
  { name: 'Approved' },
  { name: 'Exported' }
];
const approvalChildTabs = [
  { name: 'To Review' },
  { name: 'In Q&A' },
  { name: 'In Review' },
  { name: 'On Hold' },
  { name: 'Rejected' },
  { name: 'Approved' },
  { name: 'Exported' }
];
const filterMap = {
  'To Review': (item) =>
    item?.documentStatus !== WAITING &&
    item?.documentStatus !== REJECT &&
    item?.documentStatus !== ONHOLD &&
    item?.documentStatus !== IN_QNA &&
    item?.documentStatus !== APPROVED &&
    item?.documentStatus !== ATTACHED &&
    !(
      item?.exportCsvStatus === EXPORT &&
      (item?.documentStatus === APPROVED || item?.documentStatus === ATTACHED)
    ),
  'In Review': (item) => item?.documentStatus === WAITING,
  Rejected: (item) => item?.documentStatus === REJECT,
  'On Hold': (item) => item?.documentStatus === ONHOLD,
  Exported: (item) =>
    item?.exportCsvStatus === EXPORT &&
    item?.documentStatus !== POPULATED &&
    !(item?.documentType === DELIVERY_TICKET && item?.documentStatus !== ATTACHED),
  'In Q&A': (item) => item?.documentStatus === IN_QNA,
  Approved: (item) =>
    ((item?.documentStatus === APPROVED || item?.documentStatus === ATTACHED) &&
      item?.exportCsvStatus !== EXPORT) ||
    (item?.documentType === DELIVERY_TICKET && item?.documentStatus === APPROVED)
};

export const filterByDocumentStatus = (data, tabName) => {
  const filterFn = filterMap[tabName];
  return filterFn ? data?.filter(filterFn) || [] : data;
};
export const getChildTabs = (userRole, data) => {
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);

  const docAssignPermission = userDetails?.user?.permissionToAssignDocuments;
  // The On Hold tab follows the same role permission as the On Hold action button
  // on the document canvas — available to every role that has it, none that don't.
  const onHoldPermission =
    userDetails?.roleConfigurations?.permissionConfiguration?.[userDetails?.role]?.onholdDocument;
  const baseTabs = [
    { name: 'To Review' },
    { name: 'In Q&A' },
    { name: 'In Review' },
    { name: 'On Hold' },
    { name: 'Classify/Split' },
    { name: 'Rejected' },
    { name: 'Approved' },
    { name: 'Exported' }
  ];

  const approvalTabs = [
    { name: 'To Review' },
    { name: 'In Q&A' },
    { name: 'In Review' },
    { name: 'On Hold' },
    { name: 'Rejected' },
    { name: 'Approved' },
    { name: 'Exported' }
  ];

  // Push safely
  if (userRole === 'C_Level' && docAssignPermission === true) {
    baseTabs.push({ name: 'Company Documents' });
    approvalTabs.push({ name: 'Company Documents' });
  }

  // Return based on role
  const tabs =
    userRole === 'Owner' || userRole === 'Accountant' || userRole === 'Support'
      ? baseTabs
      : approvalTabs;

  return onHoldPermission ? tabs : tabs.filter(({ name }) => name !== 'On Hold');
  // Only return tabs that have matching documents
  // return tabs.filter(({ name }) => {
  //   const filtered = filterByDocumentStatus(data, name);
  //   return filtered.length > 0;
  // });
};

export const vendorChildTabs = [{ name: 'All Documents' }];

export const usersChildTabs = [{ name: 'Users' }, { name: 'User Roles' }];
export const featuresTabs = [{ name: 'Policy' }];
export const dashboardTabs = [{ name: 'Overview' }];
export const budgetChildTabs = [{ name: 'All Budgets' }];

export const clientParentTabs = [
  {
    name: 'Dashboard',
    icon: <DashboardOutlined />
  },
  {
    name: 'Documents',
    icon: <InsertDriveFileOutlined />
  }
];

export const formatLineItems = (lineItem) => {
  // Line-item values are strings in the canvas state, but the API can hand back raw
  // numbers (amounts, quantities) — e.g. the rows passed straight from the document
  // load to PO matching. Normalize to text so the cleanup below (and the callers'
  // `description.trim()`) can't hit "includes is not a function" on a number.
  const getValue = (item, name) => {
    const value = item?.find((e) => e?.name === name)?.value;
    return value === undefined || value === null ? value : String(value);
  };
  const formatted = lineItem?.map((item) => {
    const amountString = getValue(item, 'Amount');
    const amount = amountString?.includes(',') ? amountString?.replace(/,/g, '') : amountString;
    const description = getValue(item, 'Description');
    const qty = getValue(item, 'Quantity');
    const productCode = getValue(item, 'Product_Code') || '';
    const unitPrice = getValue(item, 'Unit_Price') || '';
    const uom = getValue(item, 'UOM') || '';
    return { amount, description, qty, productCode, unitPrice, uom };
  });
  return formatted;
};

export const arrangedLineitems = (lineItem) => {
  const arrangedLineitemsArray = lineItem?.map((subArray) => {
    const codeObj = subArray?.find((obj) => obj?.name === 'Product_Code');
    const descriptionObj = subArray?.find((obj) => obj?.name === 'Description');
    const unitPriceObj = subArray?.find((obj) => obj?.name === 'Unit_Price');
    const uomObj = subArray?.find((obj) => obj?.name === 'UOM');
    const quantityObj = subArray?.find((obj) => obj?.name === 'Quantity');
    const amountObj = subArray?.find((obj) => obj?.name === 'Amount');
    const taxObj = subArray?.find((obj) => obj?.name === 'tax');
    const taxCodeObj = subArray?.find((obj) => obj?.name === 'tax_code');
    const taxTypeObj = subArray?.find((obj) => obj?.name === 'tax_type');
    const miscObj = subArray?.find((obj) => obj?.name === 'miscellaneous_amount');
    const discountObj = subArray?.find((obj) => obj?.name === 'discount');
    const typeObj = subArray?.find((obj) => obj?.name === 'type');
    const itemReferenceObj = subArray?.find((obj) => obj?.name === 'itemReference');
    const glAccountObj = subArray?.find((obj) => obj?.name === 'GlAccount');
    const JobIdObj = subArray?.find((obj) => obj?.name === 'userJobId');
    const workOrderObj = subArray?.find((obj) => obj?.name === 'workOrder');
    const scopeObj = subArray?.find((obj) => obj?.name === 'scope');
    const phaseObj = subArray?.find((obj) => obj?.name === 'userPhases');
    const costIdObj = subArray?.find((obj) => obj?.name === 'userCostId');
    const costTypeObj = subArray?.find((obj) => obj?.name === 'userCostType');
    const equipmentObj = subArray?.find((obj) => obj?.name === 'equipmentNo');
    const serviceObj = subArray?.find((obj) => obj?.name === 'serviceCode');
    const glDivisionObj = subArray?.find((obj) => obj?.name === 'glDivision');
    const division2Obj = subArray?.find((obj) => obj?.name === 'division2');
    const division3Obj = subArray?.find((obj) => obj?.name === 'division3');
    const division4Obj = subArray?.find((obj) => obj?.name === 'division4');

    return [
      codeObj,
      descriptionObj,
      quantityObj,
      uomObj,
      unitPriceObj,
      amountObj,
      taxObj,
      taxCodeObj,
      taxTypeObj,
      miscObj,
      discountObj,
      typeObj,
      itemReferenceObj,
      glAccountObj,
      JobIdObj,
      workOrderObj,
      scopeObj,
      phaseObj,
      costIdObj,
      costTypeObj,
      equipmentObj,
      serviceObj,
      glDivisionObj,
      division2Obj,
      division3Obj,
      division4Obj
    ];
  });
  return arrangedLineitemsArray;
};
export const HtmlTooltip = styled(({ backgroundColor, border, className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, backgroundColor, border }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 220,
    backgroundColor: backgroundColor || Colors.Canvas_BG,
    color: Colors.WHITE,
    border: `1px solid ${border || Colors.Canvas_BG}`
  }
}));

export const BootstrapInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: '7px',
    position: 'relative',

    paddingLeft: '1em',
    '&:focus': {
      borderRadius: '7px',
      borderColor: 'none !important'
    }
  }
}));

export const buttonArray = [
  {
    name: 'Reject',
    status: 'Reject',
    icon: <CloseOutlined sx={{ width: { xs: '13px', lg: '21px' }, color: Colors.RED }} />,
    Color: Colors.RED
  },

  {
    name: 'On Hold',
    status: 'on Hold',
    icon: <PanToolOutlined sx={{ width: { xs: '13px', lg: '17px' }, color: Colors.YELLOW }} />,
    Color: Colors.YELLOW
  }
];
export const convertTime = (inputTime) => {
  const match = inputTime?.match(/(\d+)h?:?(\d*)min?:?(\d*)sec?/);
  if (!match) {
    return '0';
  }
  const [, hours, minutes, seconds] = match?.map((part) => (part === '' ? 0 : Number(part)));
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  const resultHours = Math?.floor(totalSeconds / 3600);
  const resultMinutes = Math?.floor((totalSeconds % 3600) / 60);
  const resultSeconds = totalSeconds % 60;
  let result = '';
  if (resultHours > 0) {
    result += `${resultHours}h`;
  }
  if (resultMinutes > 0) {
    result += ` ${resultMinutes}min`;
  }
  if (resultSeconds > 0) {
    result += ` ${resultSeconds}sec`;
  }
  return result;
};

export const AdminParentTabs = [
  {
    name: 'Documents',
    icon: <InsertDriveFileOutlined sx={{ display: { xs: 'none', md: 'block' } }} />
  },
  {
    name: 'Users',
    icon: <PersonOutlineOutlined sx={{ display: { xs: 'none', md: 'block' } }} />
  },
  { name: 'Vendors', icon: <CorporateFare sx={{ display: { xs: 'none', md: 'block' } }} /> },
  { name: 'Companies', icon: <Apartment sx={{ display: { xs: 'none', md: 'block' } }} /> },
  {
    name: 'Diagnostics',
    icon: <MonitorHeartOutlined sx={{ display: { xs: 'none', md: 'block' } }} />
  }
];
export const AdminChildTabs = [
  {
    name: 'All Users'
  },
  {
    name: 'Manual Users'
  }
];
export const AdminDocChildTabs = [
  {
    name: 'All Documents'
  },
  {
    name: 'Error Documents'
  }
];
export const AdminVendorsChildTabs = [
  {
    name: 'All Vendors'
  }
];
export const AdminCompaniesChildTabs = [
  {
    name: 'All Companies'
  }
];
export const AdminDiagnosticsChildTabs = [
  {
    name: 'Foundation Sync'
  }
];

export const vendorColumns = [
  {
    field: 'email',
    headerName: (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmailOutlined sx={{ color: Colors.TOURQUISE, fontSize: '17px', marginRight: '4px' }} />
        Email
      </div>
    ),
    flex: 1,
    minWidth: 240,
    sortable: false
  },
  {
    field: 'invoices',
    headerName: (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <InsertDriveFileOutlined
          sx={{ color: Colors.TOURQUISE, fontSize: '17px', marginRight: '4px' }}
        />
        No of Invoices
      </div>
    ),
    flex: 1,
    minWidth: 200,
    sortable: false
  },
  {
    field: 'pages',
    headerName: (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Pages sx={{ color: Colors.TOURQUISE, fontSize: '17px', marginRight: '4px' }} />
        No of Pages
      </div>
    ),
    flex: 1,
    minWidth: 150,
    sortable: false
  },
  {
    field: 'limit',
    headerName: (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <AssignmentLateOutlined
          sx={{ color: Colors.TOURQUISE, fontSize: '17px', marginRight: '4px' }}
        />
        Document Limit
      </div>
    ),
    flex: 1,
    minWidth: 150,
    sortable: false
  },
  {
    field: 'action',
    headerName: (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <HighlightAltOutlined
          sx={{
            color: Colors.TOURQUISE,
            fontSize: '17px',
            marginRight: '4px'
          }}
        />
        Actions
      </div>
    ),
    flex: 1,
    minWidth: 240,
    sortable: false,
    disablecolumnmenu: false,
    renderCell: (params) => (
      <>
        <MuiModal
          text="Create Bill"
          disable={params?.row?.invoices === 0}
          data={params?.row?.email}
          show="createBillVendor"
          color={Colors.TOURQUISE}
          btnHeight="2rem"
          width="5rem"
        />
      </>
    )
  },
  {
    flex: 1,
    minWidth: 240,
    sortable: false,
    disablecolumnmenu: false,
    renderCell: (params) => (
      <>
        <MuiModal
          text="View"
          data={params?.row?.documents}
          show="vendorsDocuments"
          color={Colors.TOURQUISE}
          btnHeight="2rem"
          width="5rem"
          disable={params?.row?.documents?.length === 0}
          mr=".5rem"
        />
        <MuiModal
          text="Edit"
          data={params?.row}
          show="editVendor"
          color={Colors.TOURQUISE}
          btnHeight="2rem"
          width="5rem"
        />
      </>
    )
  }
];

export const getPosition = (id, getRef) => {
  // get on-screen position of highlighted boxes
  const reference = getRef(id);
  const object = reference?.current?.getClientRects()[0];
  if (object) {
    const position = {
      width: object.width,
      height: object.height,
      left: object.left,
      top: object.top - 67
    };
    return position;
  } else {
    return null;
  }
};

export const filterDocuments = (queue) => {
  const newQueue = queue?.toReview;
  return newQueue;
};

function camelCaseToTitle(str) {
  return str?.replace(/([a-z])([A-Z])/g, '$1 $2')?.replace(/^./, (char) => char?.toUpperCase());
}
/**
 * Single source of truth for the Cost Id field's UI label.
 * The backend key stays userCostId / costIds regardless — this only affects display:
 *   - QuickBooks Desktop      → "Item" / "Items"
 *   - Foundation / VP Vista   → "Cost Code" / "Cost Codes"
 *   - everything else         → "Cost Id" / "Cost Ids"
 * Pass { plural: true } for list/count contexts (e.g. integration tab cards).
 */
export function getCostIdLabel(erpType, { plural = false } = {}) {
  if (erpType === 'QuickBooksDesktop') return plural ? 'Items' : 'Item';
  if (erpType === 'Foundation' || erpType === 'viewPointVista')
    return plural ? 'Cost Codes' : 'Cost Code';
  return plural ? 'Cost Ids' : 'Cost Id';
}

export function replaceName(name, erpType) {
  switch (name) {
    case 'Product_Code':
      return 'Product Code';
    case 'itemReference':
      return 'Item Reference';
    case 'Unit_Price':
      return 'Unit Price';
    case 'userCostId':
      return getCostIdLabel(erpType);
    case 'userCostType':
      return erpType === 'QuickBooksOnline' ? 'Class' : 'Cost Type';
    case 'userPhases':
      return 'Phase';
    case 'userJobId':
      return erpType === 'QuickBooksOnline' ? 'Project Id' : 'Job Id';
    case 'GlAccount':
      return 'Gl Account';
    case 'glDivision':
      return 'Division 1';
    case 'type':
      return 'Type';
    case 'miscellaneous_amount':
      return 'Misc Amount/Freight';
    case 'discount':
      return 'Discount';
    case 'tax_code':
      return 'Tax Code';
    case 'tax_type':
      return 'Tax Type';
    case 'division2':
      return 'Division 2';
    case 'division3':
      return 'Division 3';
    case 'division4':
      return 'Division 4';
    default:
      return camelCaseToTitle(name);
  }
}

export function getYearRange() {
  const currentYear = new Date().getFullYear();
  const prevYear = currentYear - 1;
  const prev2Year = currentYear - 2;
  const nextYears = [currentYear + 1, currentYear + 2];
  return [prev2Year, prevYear, currentYear, ...nextYears];
}

export function getVendorNameLength(erpType) {
  let vendorNameLength;
  switch (erpType) {
    case 'Foundation':
      vendorNameLength = 150;
      break;
    default:
      vendorNameLength = 41;
      break;
  }
  return vendorNameLength;
}

export function getTodaysDate() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();
  return `${month}/${day}/${year}`;
}
export const deepEqual = (obj1, obj2) => {
  if (typeof obj1 === 'object' && obj1 !== null && typeof obj2 === 'object' && obj2 !== null) {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
      return false;
    }
    for (let key in obj1) {
      if (!deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  } else {
    return obj1 === obj2;
  }
};
export const truncateFileName = (fileName, maxLength) => {
  if (!fileName) return '';
  const dotIndex = fileName?.lastIndexOf('.');
  if (dotIndex === -1) return fileName;
  const name = fileName?.substring(0, dotIndex);
  const extension = fileName?.substring(dotIndex);
  if (name?.length > maxLength) {
    return name?.substring(0, maxLength) + '...' + extension;
  }
  return fileName;
};
export const formatNumber = (number) => {
  if (isNaN(number) || number === null || number === undefined || number === '' || number === '-') {
    return number;
  }
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);

  return formattedNumber;
};

export function formatNumberWithoutDecimal(number) {
  if (isNaN(number) || number === null || number === undefined || number === '' || number === '-') {
    return number;
  }

  const numStr = number?.toString();
  const length = numStr?.length;
  if (length <= 3) {
    return numStr;
  }

  return numStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const InvoiceNumberKeys = [
  'invoice_number',
  'application_no',
  'receipt_number',
  'Estimate_no',
  'Po_number',
  'DeliveryTicket_no'
];
const createNewLineItem = (
  headers,
  total,
  jobId,
  workOrder,
  scope,
  costId,
  serviceCode,
  phase,
  equipmentNo,
  costType,
  glAccount,
  glDivision,
  division2,
  division3,
  division4,
  type,
  taxCode,
  taxType
) => {
  const values = {
    Description: 'Consolidated Items',
    Amount:
      total !== '' && total !== '-' && total !== 'N/A' ? parseFloat(total)?.toFixed(2) : total,
    userJobId: jobId,
    workOrder: workOrder,
    scope: scope,
    userCostId: costId,
    serviceCode: serviceCode,
    userPhases: phase,
    equipmentNo: equipmentNo,
    userCostType: costType,
    GlAccount: glAccount,
    glDivision: glDivision,
    division2: division2,
    division3: division3,
    division4: division4,
    type: type,
    tax_code: taxCode ?? '',
    tax_type: taxType ?? ''
  };

  return Array.from({ length: headers?.length || 0 }, (_, index) => {
    const headerName = headers[index];
    if (headerName) {
      return {
        name: headerName,
        value: values[headerName] ?? '', // Populate the corresponding value
        width: null,
        height: null,
        left: null
      };
    }
    return null;
  });
};
export const consolidateItems = (
  items,
  total,
  codes,
  costId,
  jobId,
  workOrder,
  scope,
  glAccount,
  phase,
  costType,
  serviceCode,
  equipmentNo,
  glDivision,
  division2,
  division3,
  division4,
  type,
  erpType,
  taxCode,
  taxType
) => {
  if (!isEmpty(items)) {
    const itemValues = {
      Description: 'Consolidated Items',
      Amount:
        total !== '' && total !== '-' && total !== 'N/A' ? parseFloat(total)?.toFixed(2) : total,
      userJobId: jobId,
      workOrder: workOrder,
      scope: scope,
      userCostId: costId,
      serviceCode: serviceCode,
      userPhases: phase,
      equipmentNo: equipmentNo,
      userCostType: costType,
      GlAccount: glAccount,
      glDivision: glDivision,
      division2: division2,
      division3: division3,
      division4: division4,
      type: type,
      tax_code: taxCode ?? '',
      tax_type: taxType ?? '',
      itemReference: erpType === 'viewPointVista' ? 1 : ''
    };
    const consolidatedRow = items[0]?.map((item) => {
      let value = itemValues[item?.name] ?? '';
      // Tax code/type: the header value wins when it carries one, but never
      // clobber a tax value already present on the source line item with an
      // empty header value (e.g. when the header tax wasn't passed or is blank).
      if (item?.name === 'tax_code' || item?.name === 'tax_type') {
        const headerVal = itemValues[item?.name];
        value =
          headerVal != null && String(headerVal).trim() !== '' ? headerVal : item?.value ?? '';
      }
      return {
        ...item,
        value,
        width: '',
        height: '',
        left: ''
      };
    });
    // Vista: ensure tax_code/tax_type columns exist on the consolidated row (with the
    // header value) even when the source line items don't carry them. Only adds a column
    // when the caller explicitly passes a header tax value; rows that already carry the
    // column are left to the map above (which preserves the source value when the header
    // value is blank), so it is never overwritten here.
    if (erpType === 'viewPointVista' && Array.isArray(consolidatedRow)) {
      const taxFields = { tax_code: taxCode, tax_type: taxType };
      Object.entries(taxFields).forEach(([taxName, taxVal]) => {
        if (taxVal == null) return;
        if (!consolidatedRow.some((item) => item?.name === taxName)) {
          consolidatedRow.push({
            name: taxName,
            value: taxVal,
            width: '',
            height: '',
            left: ''
          });
        }
      });
    }
    return consolidatedRow;
  } else {
    const newLineItem = createNewLineItem(
      codes,
      total,
      jobId,
      workOrder,
      scope,
      costId,
      serviceCode,
      phase,
      equipmentNo,
      costType,
      glAccount,
      glDivision,
      division2,
      division3,
      division4,
      type,
      taxCode,
      taxType
    )?.filter(Boolean);
    return newLineItem;
  }
};
export const resetObjFields = (obj) => {
  const newObj = {};
  Object?.keys(obj)?.forEach((key) => {
    newObj[key] = [];
  });
  return newObj;
};

export const formatAndFloatNumber = (num, maxFractionDigits = 2) => {
  const number = parseFloat(num);
  if (isNaN(number) || number === null || number === undefined || number === '' || number === '-') {
    return num;
  }
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(maxFractionDigits, 2)
  }).format(number);

  return formattedNumber;
};

export const getDocumentType = (userDetails, isPdf, isSinglePage) => {
  let options = ['Invoice'];
  if (
    (userDetails?.erpType === 'Foundation' || userDetails?.erpType === 'QuickBooksDesktop') &&
    userDetails?.quotePermission
  ) {
    options.push('Quote');
  }
  if (userDetails?.G702) {
    options.push('G702');
  }
  if (userDetails?.receiptUpload) {
    options.push('Receipt');
  }
  if (userDetails?.deliveryTicketUpload) {
    options.push('Delivery Ticket');
  }
  if (isPdf && !isSinglePage) {
    options.push('Split');
  }
  return options;
};
export const constructionData = [
  'vendors',
  'accounts',
  'jobIds',
  'costIds',
  'costType',
  'employees',
  'termsList',
  'paid',
  'glDivision',
  'sendErpStatus',
  'uom',
  'glDivision'
];
export const vistaConstructionData = [
  'vendors',
  'accounts',
  'jobIds',
  'costIds',
  'costType',
  'employees',
  'taxCode',
  // [reviewer-disabled] 'reviewer',
  'sendErpStatus',
  'reviewerGroup',
  'uom',
  'customField',
  'POCompanyParams'
];
export const spectrumConstructionData = [
  'vendors',
  'accounts',
  'jobIds',
  'costIds',
  'costType',
  'employees',
  'uom',
  'termsList'
];
export const spectrumBasicData = ['vendors', 'accounts', 'employees', 'uom', 'termsList'];
export const basicData = [
  'vendors',
  'accounts',
  'employees',
  'termsList',
  'paid',
  'sendErpStatus',
  'uom',
  'glDivision'
];
export const vistaBasicData = [
  'vendors',
  'accounts',
  'employees',
  'taxCode',
  'sendErpStatus',
  // [reviewer-disabled] 'reviewer',
  'reviewerGroup',
  'uom',
  'customField',
  'POCompanyParams'
];
export const otherConstructionData = ['vendors', 'accounts', 'jobIds', 'costIds', 'costType'];
// Which ERP lists to pull down, decided by the fields the company actually has.
//
// This used to read three company flags: constructionCompany picked the "construction"
// list over the "basic" one (the difference being jobIds/costIds/costType),
// serviceCodeAndEquipmentNo added equipment/serviceCode, and phasePermission and
// workOrderPermission added their own. All four are now the fields themselves, so the
// base list per ERP is the basic one and every coding list is added by its own field.
// poMatchPermission is untouched — it is still a live company permission.
//
// `isFieldOn` comes from utils/fieldConfig; before the config has loaded it answers
// "on", so a first sync pulls everything rather than too little.
export function getSyncArray(erpType, isFieldOn, userPermission) {
  const base = {
    Foundation: basicData,
    viewPointVista: vistaBasicData,
    viewPointSpectrum: spectrumBasicData
  };
  const syncArray = [...(base[erpType] || otherBasicData)];

  const addIf = (name, ...lists) => {
    if (isFieldOn(name)) syncArray.push(...lists);
  };

  addIf('userJobId', 'jobIds');
  addIf('userCostId', 'costIds');
  addIf('userCostType', 'costType');
  addIf('equipmentNo', 'equipment');
  addIf('serviceCode', 'serviceCode');
  addIf('userPhases', 'phase');
  addIf('workOrder', 'workOrder', 'scope');

  if (userPermission?.poMatchPermission) {
    syncArray.push('purchaseOrder');
    syncArray.push('subContract');
  }
  return [...new Set(syncArray)];
}

export const otherBasicData = ['vendors', 'accounts'];
export const replaceStatus = (status) => {
  switch (status) {
    case 'Populated':
      return 'Pending';
    case 'Waiting For Approval':
      return 'In Review';
    case 'Pending':
      return 'Processing';
    case 'Generated':
      return 'Attached';
    case 'Reject':
      return 'Rejected';
    case 'verified':
      return 'In ERP';
    default:
      return status;
  }
};

export const defaultFilter = {
  search: false,
  filter: false,
  sort: '',
  createdAt: { to: '', from: '' },
  amount: { min: '', max: '' },
  sendByEmail: false,
  status: [],
  paymentStatus: '',
  invoiceNo: '',
  currentOwner: '',
  vendor: '',
  glAccount: '',
  jobId: '',
  phase: '',
  costId: '',
  costType: '',
  equipmentNo: '',
  serviceCode: '',
  updatedAt: { to: '', from: '' },
  documentType: [],
  payControl: '',
  fileUploadedBy: '',
  mentions: '',
  qnaCreatedAt: { from: '', to: '' },
  exportedAt: { from: '', to: '' }
};

export function getIntegrationTabs(erpType, activeIntegration) {
  const hideManualSyncing = [{ name: 'My Integration' }];
  const hideMyIntegration = [{ name: 'Manual Syncing' }];
  const showMyIntegration = [{ name: 'My Integration' }, { name: 'Manual Syncing' }];

  let integrationTabs =
    erpType === 'DeltekComputerEase'
      ? hideMyIntegration
      : activeIntegration
      ? hideManualSyncing
      : showMyIntegration;

  if (erpType === 'Foundation' && activeIntegration !== '') {
    integrationTabs = [...integrationTabs, { name: 'Search Integration' }];
  }

  return integrationTabs;
}
export const getParentTabs = (userRole) => {
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const rolePermissions = userDetails?.roleConfigurations?.permissionConfiguration;
  const currentUserRole = userDetails?.role;
  const policyPermission = rolePermissions?.[currentUserRole]?.updatePolicy;
  let parentTabs = [
    {
      name: 'Dashboard',
      icon: <DashboardOutlined />
    },
    {
      name: 'Documents',
      icon: <InsertDriveFileOutlined />
    },
    { name: 'Integration', icon: <CorporateFare /> }
  ];

  if (policyPermission) {
    parentTabs.push({ name: 'Policy', icon: <FindInPageOutlined /> });
  }

  const budgetPermission =
    userDetails?.company?.jobBudgetManagement ??
    userDetails?.companyPermission?.jobBudgetManagement;
  const isQuickBooksDesktop = userDetails?.user?.erpType === QUICKBOOKS_DESKTOP_ERP;

  if (budgetPermission && isQuickBooksDesktop) {
    parentTabs.push({ name: 'Budgets', icon: <AttachMoney /> });
  }

  parentTabs.push({ name: 'Downloads', icon: <FileDownloadOutlined /> });

  return parentTabs;
};

export const rearrangeFields = (fields) => {
  const endFieldsOrder = [
    'payment_status',
    'payment_type',
    'check_no',
    'payment_amount',
    'payment_date'
  ];
  const endFields = fields?.filter((field) => endFieldsOrder?.includes(field?.name));
  const remainingFields = fields?.filter((field) => !endFieldsOrder?.includes(field?.name));
  const orderedEndFields = endFields?.sort(
    (a, b) => endFieldsOrder?.indexOf(a?.name) - endFieldsOrder?.indexOf(b?.name)
  );

  return [...remainingFields, ...orderedEndFields];
};

export function renameDocumentType(annotationType) {
  if (annotationType === ESTIMATE) {
    return 'Quote';
  }

  if (annotationType === DELIVERY_TICKET) {
    return 'Delivery Ticket';
  }

  if (annotationType === PURCHASE_ORDER) {
    return 'PO';
  }

  return annotationType;
}
export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date) ? date : null;
};
export const computeDocumentCounts = (documents) => {
  if (!documents) return { Exported: 0, Approved: 0 };

  const Exported =
    documents.filter(
      (item) =>
        item?.exportCsvStatus === EXPORT &&
        !(item?.documentType === DELIVERY_TICKET && item?.documentStatus !== ATTACHED)
    ).length || 0;

  const Approved =
    documents.filter(
      (item) =>
        (item?.documentStatus === APPROVED && item?.exportCsvStatus !== EXPORT) ||
        (item?.documentType === DELIVERY_TICKET && item?.documentStatus === APPROVED)
    ).length || 0;

  return { Exported, Approved };
};

// Shape mirrors the ERP purchase-order-content endpoint so fetched data can be
// stored and sent back verbatim. Fields flagged `hideOnPo` are editable on the
// left card but are intentionally kept off the printable PO preview image.
export const documentContentFields = [
  { name: 'vendor', value: '' },
  { name: 'Po_date', value: getTodaysDate() },
  // Estimate_no is the ERP key for what users know as "Order no". It shows on the
  // PO preview (labeled "Order no") but is still sent to the API as Estimate_no.
  { name: 'Estimate_no', value: '' },
  { name: 'total', value: '' },
  { name: 'shipping_address', value: '' },
  { name: 'freight', value: '' },
  { name: 'tax', value: '' },
  { name: 'purchase_order', value: '', hideOnPo: true },
  { name: 'retention_amount', value: '', hideOnPo: true },
  { name: 'notes', value: '', hideOnPo: true }
];

// A single ERP line item, keyed exactly as the integration endpoints expect.
// The first five are the user-facing core fields; the rest are ERP metadata
// that the cache may or may not populate.
export const emptyPoLineItem = () => ({
  Description: '',
  Quantity: '',
  Unit_Price: '',
  Amount: '',
  UOM: '',
  Product_Code: '',
  type: '',
  GlAccount: '',
  userJobId: '',
  userPhases: '',
  userCostId: '',
  userCostType: '',
  equipmentNo: '',
  serviceCode: '',
  tax: '',
  itemReference: '',
  miscellaneous_amount: ''
});

export function applyPhasePermission(baseArray, isPhaseAllowed) {
  if (!isPhaseAllowed) return baseArray;

  const updated = [...baseArray];

  // Insert Phase & User Phases as new dropdown items
  updated?.splice(5, 0, { name: 'Phase', value: ['phase'] });

  // Update "All" bucket
  const allIndex = updated?.findIndex((item) => item.name === 'All');
  if (allIndex !== -1) {
    updated[allIndex] = {
      ...updated[allIndex],
      value: [...updated[allIndex].value, 'phase']
    };
  }

  return updated;
}
export function applyWorkOrderPermission(baseArray, isWorkOrderAllowed) {
  if (!isWorkOrderAllowed) return baseArray;

  const updated = [...baseArray];

  // Insert Work Order as new dropdown items
  updated?.splice(5, 0, { name: 'Work Order', value: ['workOrder'] });

  // Update "All" bucket
  const allIndex = updated?.findIndex((item) => item.name === 'All');
  if (allIndex !== -1) {
    updated[allIndex] = {
      ...updated[allIndex],
      value: [...updated[allIndex].value, 'workOrder']
    };
  }

  return updated;
}
export function applyPoMatchPermission(baseArray, poMatchPermission) {
  if (!poMatchPermission) return baseArray;

  const updated = [...baseArray];

  // Insert Purchase Order and Subcontract as new dropdown items
  updated?.splice(updated?.length - 1, 0, { name: 'Purchase Order', value: ['purchaseOrder'] });
  updated?.splice(updated?.length - 1, 0, { name: 'Subcontract', value: ['subContract'] });

  // Update "All" bucket
  const allIndex = updated?.findIndex((item) => item?.name === 'All');
  if (allIndex !== -1) {
    updated[allIndex] = {
      ...updated[allIndex],
      value: [...updated[allIndex]?.value, 'purchaseOrder', 'subContract']
    };
  }

  return updated;
}
export const calculateTax = (e, taxIndex, totalAmount, handleFieldChange) => {
  const rawValue = e?.target?.value?.toString() || '';

  const isPercentage = rawValue?.includes('%');

  const cleanedValue = rawValue?.replace(/[^0-9.]/g, '');
  const numericValue = parseFloat(cleanedValue);

  if (isNaN(numericValue)) return;

  let finalAmount;

  if (isPercentage) {
    const total = Number(totalAmount) || 0;
    finalAmount = formatDistributedValue((numericValue / 100) * total);
  } else {
    finalAmount = formatDistributedValue(numericValue);
  }

  handleFieldChange({ target: { value: finalAmount } }, taxIndex, 'Amount');
};

const sanitizeNumber = (val) => {
  if (!val) return NaN;

  return parseFloat(
    val?.toString()?.replace(/[^0-9.-]/g, '') // removes $, commas, spaces, etc
  );
};

export const isAmountMismatch = (rowIndex, obj, lineItemAmountCalculationPermission) => {
  if (!lineItemAmountCalculationPermission) return false;

  const qty = sanitizeNumber(obj?.Quantity?.[rowIndex]);
  const unitPrice = sanitizeNumber(obj?.Unit_Price?.[rowIndex]);
  const amount = sanitizeNumber(obj?.Amount?.[rowIndex]);

  if (isNaN(qty) || isNaN(unitPrice) || isNaN(amount)) {
    return false;
  }

  const calculated = qty * unitPrice;

  // allow small floating precision difference
  return Math.abs(calculated - amount) > 0.01;
};
export const getCalculatedAmount = (rowIndex, obj) => {
  const qty = sanitizeNumber(obj?.Quantity?.[rowIndex]);
  const unitPrice = sanitizeNumber(obj?.Unit_Price?.[rowIndex]);

  if (isNaN(qty) || isNaN(unitPrice)) return '';
  const val = qty * unitPrice;
  if (isNaN(val)) return '';

  return val?.toFixed(2);
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '$0.00';
  if (value === '-' || value === 'N/A') return value;
  const numeric = Number(String(value)?.replace(/[^0-9.-]+/g, ''));
  if (isNaN(numeric)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })?.format(numeric);
};

export const getStatusKey = (tab) => {
  const statusMap = {
    'To Review': 'toReview',
    'In Q&A': 'In Q&A',
    'In Review': 'Waiting For Approval',
    'On Hold': 'on Hold',
    Rejected: 'Reject',
    Approved: 'Approved',
    Exported: 'Exported'
  };

  return statusMap[tab] || 'toReview';
};

const TAB_TO_KEY_MAP = {
  'To Review': 'toReview',
  'In Q&A': 'inQandA',
  'In Review': 'inReview',
  'On Hold': 'onHold',
  Rejected: 'rejected',
  Approved: 'approved',
  Exported: 'exported',
  Pending: 'pending',
  Error: 'error',
  Attached: 'attached',
  Duplicate: 'duplicate'
};

export const getStatusKeyFromTab = (tab) => {
  return TAB_TO_KEY_MAP[tab] || null;
};

export const getSortLabel = (field) => {
  const map = {
    vendorName: 'Vendor',
    documentType: 'Document Type',
    sendByEmail: 'Send By Email',
    receivedAt: 'createdAt',
    documentName: 'Document Name',
    dueDate: 'Document Date',
    documentId: 'Job ID',
    equipmentNo: 'Equipment No',
    paid: 'Paid',
    comment: 'Comment',
    invoiceNo: 'Document No',
    amountDue: 'Amount Due',
    poNumber: 'PO Number',
    status: 'Status',
    fileUploadedBy: 'fileUploadedBy',
    mentions: 'mentions',
    qnaCreatedAt: 'qnaCreatedAt',
    exportedAt: 'exportedAt'
  };

  return map[field] || field;
};

export const getDocumentStatusName = (documentStatus) => {
  const status = documentStatus?.toString()?.toLowerCase();

  if (status === ONHOLD?.toLowerCase()) return [ONHOLD];
  if (status === REJECT?.toLowerCase()) return [REJECT];
  if (status === IN_REVIEW?.toLowerCase()) return [WAITING];
  if (status === ATTACHED?.toLowerCase()) return [ATTACHED];
  if (status === PENDING?.toLowerCase()) return [POPULATED];
  if (status === PROCESSING?.toLowerCase()) return [PENDING];
  if (status === DUPLICATE?.toLowerCase()) return [DUPLICATE];
  if (status === IN_QNA?.toLowerCase()) return [IN_QNA];
  if (status === APPROVED?.toLowerCase()) return [APPROVED];
  if (status === ERROR?.toLowerCase()) return [ERROR];

  return [];
};

// Builds the /get-document request body from the persisted DataGrid filters
// (Redux `tableFilters`) merged over any advanced-search filters. EVERY place
// that refetches the queue after mutating a document (approve, save, delete,
// pull-back, send-to-ERP, split, duplicate, upload) must send this SAME payload.
// Otherwise the refetch dispatches an UNFILTERED queue into Redux, which the home
// page renders as-is — silently clearing the user's active table filter (e.g. Pay
// Control) until they log out. Mirrors the home page's getDocumentRef payload and
// canvas buildTableQueryPayload.
// qnaCreatedAt (In Q&A tab) and exportedAt (Exported tab) are the date-range
// column filters: their value is a { from, to } object rather than a plain
// string. These helpers keep the "is a filter active" and "read the range"
// logic identical across every payload builder.
export const DATE_RANGE_FILTER_FIELDS = ['qnaCreatedAt', 'exportedAt'];
export const getDateRangeFilter = (filterItem, field) => {
  const value = filterItem?.field === field ? filterItem?.value : null;
  return { from: value?.from || '', to: value?.to || '' };
};
export const getQnaCreatedAtRange = (filterItem) => getDateRangeFilter(filterItem, 'qnaCreatedAt');
export const getExportedAtRange = (filterItem) => getDateRangeFilter(filterItem, 'exportedAt');
// documentType is the one multi-select column filter: the API expects an array of
// ERP document-type values (e.g. ['Invoice', 'Receipt']), so normalize whatever the
// grid holds — a single pick or a multi-select array — into that shape.
export const getDocumentTypeFilter = (filterItem) => {
  if (filterItem?.field !== 'documentType') return [];
  const value = filterItem?.value;
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};
export const isFilterItemActive = (filterItem) => {
  if (DATE_RANGE_FILTER_FIELDS.includes(filterItem?.field)) {
    return Boolean(filterItem?.value?.from || filterItem?.value?.to);
  }
  // An empty multi-select is no filter at all.
  if (filterItem?.field === 'documentType') {
    return getDocumentTypeFilter(filterItem).length > 0;
  }
  return filterItem?.value !== undefined && filterItem?.value !== null && filterItem?.value !== '';
};

export const buildFilteredDocPayload = (tableFilters, savedFilters) => {
  const payload = { ...(!isEmpty(savedFilters) ? savedFilters : defaultFilter) };

  const filterItem = tableFilters?.dataGridFilterModel?.items?.[0];
  const hasActiveFilter = isFilterItemActive(filterItem);

  payload.filter = hasActiveFilter;
  payload.vendor = filterItem?.field === 'vendorName' ? filterItem?.value : '';
  payload.equipmentNo = filterItem?.field === 'equipmentNo' ? filterItem?.value : '';
  payload.currentOwner = filterItem?.field === 'currentOwner' ? filterItem?.value : '';
  payload.payControl = filterItem?.field === 'payControl' ? filterItem?.value : '';
  payload.fileUploadedBy = filterItem?.field === 'fileUploadedBy' ? filterItem?.value : '';
  payload.mentions = filterItem?.field === 'mentions' ? filterItem?.value : '';
  payload.qnaCreatedAt = getQnaCreatedAtRange(filterItem);
  payload.exportedAt = getExportedAtRange(filterItem);
  // DataGrid column is named 'documentId' but the API expects it under `jobId`.
  payload.jobId = filterItem?.field === 'documentId' ? filterItem?.value : '';
  payload.status =
    (filterItem?.field === 'status' && getDocumentStatusName(filterItem?.value)) || [];
  payload.documentType = getDocumentTypeFilter(filterItem);

  const sortModel = tableFilters?.dataGridSortModel?.[0];
  if (sortModel?.field && sortModel?.sort) {
    payload.sort = `${getSortLabel(sortModel.field)} ${sortModel.sort}`;
  }

  return payload;
};
